import { useEffect, useState } from "react";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Header from "../../../components/Header/Header";
import useAxios from "../../../hooks/useAxios";
import "./ActiveSessionsPage.css";
import StyledSelect from "../../../components/common/StyledComponents/StyledSelect";
import { MenuItem, Button } from "@mui/material";
import AnswerRow from "./AnswerRow";
import RefreshIcon from "@mui/icons-material/Refresh";
import { useLocation } from "react-router-dom";

const navLinks = [];

function ActiveSessionsPage() {
  const location = useLocation();
  const enableClassFilter = location.state?.enableClassFilter || false;
  const axiosInstance = useAxios();
  const [quizzes, setQuizzes] = useState([]);
  const [classes, setClasses] = useState([]);
  const [filteredQuizzes, setFilteredQuizzes] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [selectedQuiz, setSelectedQuiz] = useState(null);
  const [selectedClass, setSelectedClass] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchQuizzesAndClasses = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axiosInstance.get("/quizzes/owner");
      if (response.data.success) {
        const quizzes = response.data.quizzes;
        console.log("Fetched quizzes:", quizzes); // Debug log
        console.log(
          "Quiz classes:",
          quizzes.map((q) => ({
            id: q.id,
            title: q.title,
            class: q.class,
            classes: q.classes,
          }))
        ); // Debug log

        // Extract classes from both legacy class and attached_classes
        const classes = [];

        // First, get classes from legacy class relationship
        const legacyClasses = quizzes
          .filter((quiz) => quiz.class)
          .map((quiz) => quiz.class);

        // Then, get classes from attached_classes (new system)
        const attachedClasses = quizzes
          .filter(
            (quiz) => quiz.attached_classes && quiz.attached_classes.length > 0
          )
          .flatMap((quiz) => quiz.attached_classes);

        // Combine and deduplicate
        const allClasses = [...legacyClasses, ...attachedClasses];
        const uniqueClasses = allClasses.filter(
          (value, index, self) =>
            self.findIndex((c) => c.id === value.id) === index
        );

        classes.push(...uniqueClasses);
        console.log("Extracted classes:", classes); // Debug log
        setQuizzes(quizzes);
        setClasses(classes);
        setFilteredQuizzes(quizzes);
      } else {
        toast.error(response.data.message || "Failed to fetch Assessments.");
      }
    } catch (err) {
      toast.error(
        err.response?.data?.message || "An unexpected error occurred."
      );
    } finally {
      setLoading(false);
    }
  };

  const fetchReport = async (quizId) => {
    setLoading(true);
    setError(null);
    try {
      // This calls the updated back-end route that now has grouped_answers
      const response = await axiosInstance.get(
        `/reporting/${quizId}/active-sessions`
      );
      if (response.data.success) {
        setSessions(response.data.sessions);
      } else {
        toast.error(response.data.message || "Failed to fetch sessions.");
      }
    } catch (err) {
      toast.error(
        err.response?.data?.message || "An unexpected error occurred."
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchQuizzesAndClasses();
  }, []);

  const handleSelectQuiz = (event) => {
    const quizId = event.target.value;
    setSelectedQuiz(quizId);
    fetchReport(quizId);
  };

  const handleSelectClass = (event) => {
    const classId = event.target.value;
    setSelectedClass(classId);

    if (classId) {
      const filtered = quizzes.filter((quiz) => {
        // Check legacy class relationship
        if (quiz.class?.id === classId) {
          return true;
        }
        // Check attached classes (new system)
        if (
          quiz.attached_classes &&
          quiz.attached_classes.some((cls) => cls.id === classId)
        ) {
          return true;
        }
        return false;
      });
      setFilteredQuizzes(filtered);
      setSelectedQuiz("");
    } else {
      setFilteredQuizzes(quizzes);
    }
  };

  const handleReload = () => {
    if (selectedQuiz) {
      fetchReport(selectedQuiz);
    }
  };

  return (
    <>
      <Header navLinks={navLinks} isScrollEffect={false} />
      <div className="page-content">
        <div className="active-sessions-container">
          <div className="header-row">
            <h1>Active Sessions</h1>
            <div className="controls">
              {enableClassFilter && (
                <StyledSelect
                  className="class-select"
                  value={selectedClass || ""}
                  onChange={handleSelectClass}
                  displayEmpty
                  inputProps={{ "aria-label": "Select a class" }}
                >
                  <MenuItem value="">All Classes</MenuItem>
                  {classes.map((classItem) => (
                    <MenuItem value={classItem.id} key={classItem.id}>
                      {classItem.class_name}
                    </MenuItem>
                  ))}
                </StyledSelect>
              )}
              <StyledSelect
                className="quiz-select"
                value={selectedQuiz || ""}
                onChange={handleSelectQuiz}
                displayEmpty
                inputProps={{ "aria-label": "Select an Assessment" }}
              >
                <MenuItem value="" disabled>
                  Select an Assessment
                </MenuItem>
                {filteredQuizzes.map((quiz) => (
                  <MenuItem value={quiz.id} key={quiz.id}>
                    {quiz.title}
                  </MenuItem>
                ))}
              </StyledSelect>

              <div className="reload-btn-container" onClick={handleReload}>
                <div className="reload-tooltip">Reload</div>
                <button className="reload-button">
                  <span>
                    <RefreshIcon />
                  </span>
                </button>
              </div>
            </div>
          </div>

          {loading && (
            <div className="loading-state">
              <div className="spinner"></div>
              <p>Loading active sessions...</p>
            </div>
          )}

          {error && (
            <div className="error-state">
              <p>{error}</p>
            </div>
          )}

          {!loading && !error && selectedQuiz && sessions.length === 0 && (
            <div className="no-data-state">
              <p>No active sessions found for this Assessment.</p>
            </div>
          )}

          {!loading && !error && selectedQuiz && sessions.length > 0 && (
            <div className="sessions-grid">
              {sessions.map((session) => {
                const user = session.user;
                const isStudent = user.role === "student";
                const name =
                  isStudent && user.student_profile
                    ? user.student_profile.full_name
                    : user.family_profile
                    ? user.family_profile.family_name
                    : user.username;

                // Compute progress if desired
                const totalTasks = session.quiz.questions.reduce((count, q) => {
                  return (
                    count + 1 + (q.subquestions ? q.subquestions.length : 0)
                  );
                }, 0);

                const attemptedTasks = new Set();
                (session.user_answers || []).forEach((a) => {
                  const taskId = `q${a.question_id}-s${
                    a.subquestion_id || "main"
                  }`;
                  attemptedTasks.add(taskId);
                });
                const answeredCount = attemptedTasks.size;
                const progress = Math.round((answeredCount / totalTasks) * 100);

                return (
                  <div className="session-card" key={session.id}>
                    <div className="session-header">
                      <div className="user-info">
                        <div className="avatar">{name.charAt(0)}</div>
                        <div className="user-details">
                          <p className="user-name">{name}</p>
                          <p className="user-role">{user.role}</p>
                        </div>
                      </div>
                      <div className="session-attempt">
                        Attempt: {session.attempt}
                      </div>
                    </div>

                    {/* Show the progress bar if you like */}
                    <div className="progress-section">
                      <p className="progress-label">
                        {answeredCount} / {totalTasks} questions attempted
                      </p>
                      <div className="progress-bar-container">
                        <div
                          className="progress-bar-fill"
                          style={{ width: `${progress}%` }}
                        ></div>
                      </div>
                    </div>

                    {/* Now use session.grouped_answers instead of session.user_answers */}
                    <div className="answers-section">
                      {Object.entries(session.grouped_answers || {}).map(
                        ([questionId, group]) => {
                          const { main_answer, subanswers, rollup_answer } =
                            group;

                          // 1) If there's a real main question answer (not roll-up)
                          if (main_answer && !main_answer.is_rollup) {
                            return (
                              <AnswerRow
                                key={main_answer.id}
                                answer={main_answer}
                              />
                            );
                          }

                          // 2) If subanswers exist but no main_answer => show a top-level header
                          if (!main_answer && subanswers.length > 0) {
                            const questionBody =
                              subanswers[0].question?.body ||
                              "No question text";

                            return (
                              <div key={questionId}>
                                <div className="answer-row main-question-card">
                                  <div className="answer-main">
                                    <span className="question-body">
                                      {questionBody}
                                    </span>
                                  </div>
                                </div>

                                {subanswers.map((sub) => (
                                  <AnswerRow key={sub.id} answer={sub} />
                                ))}
                              </div>
                            );
                          }

                          // Optionally handle rollup if you want:
                          // if (rollup_answer) { ... }

                          return null;
                        }
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
      <ToastContainer />
    </>
  );
}

export default ActiveSessionsPage;
